import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, sendOTP, verifyOTP } = useAuth();
  const navigate = useNavigate();

  const routeForRole = (role) => {
    if (role === 'admin') return '/admin/dashboard';
    if (role === 'hotel') return '/hotel/dashboard';
    return '/';
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      toast.success('Login successful!');
      navigate(routeForRole(result.data?.user?.role));
    } else {
      toast.error(result.error);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await sendOTP(phone);
    setLoading(false);
    if (result.success) {
      setOtpSent(true);
      toast.success(result.data.message);
    } else {
      toast.error(result.error);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await verifyOTP(phone, otp);
    setLoading(false);
    if (result.success) {
      if (result.data.user_exists) {
        toast.success('OTP verified! Logged in.');
        navigate(routeForRole(result.data?.user?.role));
      } else {
        toast.info('OTP verified! Please complete registration.');
        navigate('/register', { state: { phone } });
      }
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-lg border border-slate-200 shadow-lg">
          <h2 className="text-3xl font-black tracking-tighter text-slate-900 mb-6">{t('auth.signIn')}</h2>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="email" data-testid="email-tab">{t('auth.email')}</TabsTrigger>
              <TabsTrigger value="mobile" data-testid="mobile-tab">{t('auth.phone')} OTP</TabsTrigger>
            </TabsList>

            <TabsContent value="email">
              <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
                <div>
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#FF0059] hover:bg-[#CC0047]" disabled={loading} data-testid="email-login-submit">
                  {loading ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="mobile">
              {!otpSent ? (
                <form onSubmit={handleSendOTP} className="space-y-4" data-testid="mobile-otp-form">
              <div>
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (!v.startsWith('+91') && !v.startsWith('+')) {
                      v = '+91' + v.replace(/^\+?91?/, '');
                    }
                    setPhone(v);
                  }}
                  onFocus={() => { if (!phone) setPhone('+91'); }}
                  required
                  data-testid="phone-input"
                />
              </div>
                  <Button type="submit" className="w-full bg-[#FF0059] hover:bg-[#CC0047]" disabled={loading} data-testid="send-otp-button">
                    {loading ? '…' : t('auth.sendOtp')}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-4" data-testid="verify-otp-form">
                  <div>
                    <Label htmlFor="otp">Enter OTP</Label>
                    <Input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      data-testid="otp-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#FF0059] hover:bg-[#CC0047]" disabled={loading} data-testid="verify-otp-button">
                    {loading ? '…' : t('auth.verifyOtp')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setOtpSent(false)}
                    data-testid="change-number-button"
                  >
                    Change Number
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center text-sm text-slate-600">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-[#FF0059] hover:underline font-bold" data-testid="register-link">
              {t('auth.signUp')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};